import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, forkJoin, of } from "rxjs";
import { map, catchError } from "rxjs/operators";
import { DomSanitizer, SafeUrl } from "@angular/platform-browser";

import { StorageService } from "./storage.service";
import { EmployeeApiService } from "../api/employee-api.service";
import { EmployeeField, IEmployeeDto, IEmployeeResponse } from "../models/employee.model";

export interface IAvatarData {
	employee_id: string;
	avatar: string;
	employee_fullname: string;
	safeUrl?: SafeUrl;
}

@Injectable({
	providedIn: "root"
})
export class AvatarService {
	private avatarsSubject = new BehaviorSubject<Map<string, IAvatarData>>(new Map());
	public avatars$ = this.avatarsSubject.asObservable();

	private loadingSubject = new BehaviorSubject<boolean>(false);
	public loading$ = this.loadingSubject.asObservable();

	private employeeFields: EmployeeField[] = [EmployeeField.EmployeeId, EmployeeField.Fullname];

	constructor(private storageService: StorageService, private employeeApiService: EmployeeApiService, private domSanitizer: DomSanitizer) {
		this.loadCachedAvatars();
	}

	/**
	 * Load cached avatars from storage on service initialization
	 */
	private async loadCachedAvatars(): Promise<void> {
		try {
			const cachedAvatars: IAvatarData[] = (await this.storageService.get("employeeAvatars")) || [];
			const avatarMap = new Map<string, IAvatarData>();

			cachedAvatars.forEach((avatar) => {
				if (avatar.employee_id && avatar.avatar) {
					avatar.safeUrl = this.domSanitizer.bypassSecurityTrustUrl("data:image/png;base64," + avatar.avatar);
					avatarMap.set(avatar.employee_id, avatar);
				}
			});

			this.avatarsSubject.next(avatarMap);
		} catch (error) {
			console.error("Error loading cached avatars:", error);
		}
	}

	/**
	 * Preload all employee avatars after login
	 */
	async preloadAllAvatars(): Promise<void> {
		this.loadingSubject.next(true);

		try {
			// Clear existing cached avatars to ensure fresh data
			await this.storageService.remove("employeeAvatars");

			// Get all employees
			const employeeResponse: IEmployeeResponse = await this.employeeApiService.getEmployees(1, this.employeeFields).toPromise();

			if (!employeeResponse || !employeeResponse.data) {
				this.loadingSubject.next(false);
				return;
			}

			// Create observable array for concurrent avatar loading
			const avatarRequests = employeeResponse.data.map((employee) =>
				this.employeeApiService.getEmployeeAvatar(employee.employee_id).pipe(
					map(async (avatarBlob) => {
						if (avatarBlob) {
							const base64Avatar = await this.blobToBase64(avatarBlob);
							const safeUrl = this.domSanitizer.bypassSecurityTrustUrl("data:image/png;base64," + base64Avatar);

							return {
								employee_id: employee.employee_id,
								avatar: base64Avatar,
								employee_fullname: employee.v_display_name,
								safeUrl: safeUrl
							} as IAvatarData;
						}
						return null;
					}),
					catchError((error) => {
						console.error(`Error loading avatar for employee ${employee.employee_id}:`, error);
						return of(null);
					})
				)
			);

			// Load all avatars concurrently
			const avatarResults = await forkJoin(avatarRequests).toPromise();
			const resolvedAvatars = await Promise.all(avatarResults);

			// Filter out null results and create avatar map
			const avatarMap = new Map<string, IAvatarData>();
			const avatarsForStorage: IAvatarData[] = [];

			resolvedAvatars.forEach((avatar) => {
				if (avatar && avatar.employee_id) {
					avatarMap.set(avatar.employee_id, avatar);
					// Don't store safeUrl in storage, only the base data
					avatarsForStorage.push({
						employee_id: avatar.employee_id,
						avatar: avatar.avatar,
						employee_fullname: avatar.employee_fullname
					});
				}
			});

			// Update storage and subjects
			await this.storageService.set("employeeAvatars", avatarsForStorage);
			this.avatarsSubject.next(avatarMap);
		} catch (error) {
			console.error("Error preloading avatars:", error);
		} finally {
			this.loadingSubject.next(false);
		}
	}

	/**
	 * Get avatar SafeUrl for a specific employee
	 */
	getAvatarUrl(employeeId: string): SafeUrl | null {
		const avatarMap = this.avatarsSubject.value;
		const avatar = avatarMap.get(employeeId);
		return avatar?.safeUrl || null;
	}

	/**
	 * Get avatar data for a specific employee
	 */
	getAvatarData(employeeId: string): IAvatarData | null {
		const avatarMap = this.avatarsSubject.value;
		return avatarMap.get(employeeId) || null;
	}

	/**
	 * Check if avatar exists for employee
	 */
	hasAvatar(employeeId: string): boolean {
		const avatarMap = this.avatarsSubject.value;
		return avatarMap.has(employeeId);
	}

	/**
	 * Get observable for a specific employee's avatar
	 */
	getAvatar$(employeeId: string): Observable<SafeUrl | null> {
		return this.avatars$.pipe(
			map((avatarMap) => {
				const avatar = avatarMap.get(employeeId);
				return avatar?.safeUrl || null;
			})
		);
	}

	/**
	 * Load individual avatar if not found in cache
	 */
	async loadMissingAvatar(employeeId: string, employeeFullname?: string): Promise<void> {
		if (this.hasAvatar(employeeId)) {
			return; // Avatar already exists
		}

		try {
			const avatarBlob = await this.employeeApiService.getEmployeeAvatar(employeeId).toPromise();
			if (avatarBlob) {
				const base64Avatar = await this.blobToBase64(avatarBlob);
				const safeUrl = this.domSanitizer.bypassSecurityTrustUrl("data:image/png;base64," + base64Avatar);

				const avatarData: IAvatarData = {
					employee_id: employeeId,
					avatar: base64Avatar,
					employee_fullname: employeeFullname || "",
					safeUrl: safeUrl
				};

				// Update map
				const currentMap = new Map(this.avatarsSubject.value);
				currentMap.set(employeeId, avatarData);
				this.avatarsSubject.next(currentMap);

				// Update storage
				const storageAvatars = (await this.storageService.get("employeeAvatars")) || [];
				storageAvatars.push({
					employee_id: employeeId,
					avatar: base64Avatar,
					employee_fullname: employeeFullname || ""
				});
				await this.storageService.set("employeeAvatars", storageAvatars);
			}
		} catch (error) {
			console.error(`Error loading missing avatar for ${employeeId}:`, error);
		}
	}

	/**
	 * Convert Blob to Base64
	 */
	private blobToBase64(blob: Blob): Promise<string> {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onload = () => {
				const base64String = (reader.result as string).split(",")[1];
				resolve(base64String);
			};
			reader.onerror = reject;
			reader.readAsDataURL(blob);
		});
	}

	/**
	 * Clear all avatars from cache and storage
	 */
	async clearAvatars(): Promise<void> {
		await this.storageService.remove("employeeAvatars");
		this.avatarsSubject.next(new Map());
	}
}
